import { useEffect, useState } from 'react';
import ButtonSpinner from '../ButtonSpinner';
import { BTN_SECONDARY_SM } from '../buttonStyles';
import { TEXT_ACTION } from './surfaces';
import { WeeklyTimeSheet } from './WeeklyTimeSheet';
import { busyStyle } from '../../utils/busyStyle';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../hooks/useToast';
import { useUserIdentity } from '../../hooks/useUserIdentity';
import { useViewerZone } from '../../hooks/useViewerZone';
import { subscribeMembership } from '../../services/liveMemberships';
import { answerLiveWeeklyTimeProposal, liveLessonsErrorMessage } from '../../services/liveLessons';
import { firstNameOf } from '../../utils/liveLessonText';
import { markBookingsQuiet, weeklyTimeToast } from '../../utils/oneToast';
import { proposalLines } from '../../utils/weeklyProposal';
import type { Message } from '../../types/chat';
import type { LiveMembership } from '../../types/liveCall';

/**
 * The coach's weekly time suggestion in the thread (decision 96): up to
 * three times as chips, Keep mine (only when the member has a weekly time)
 * and Pick something else, which opens the weekly time picker. A chip sets
 * that weekly time with one tap. The server updates the card in place; only
 * an open card is tappable, and an answered one says what happened.
 */
export function WeeklyProposalBubble({ message }: { message: Message }) {
  const p = message.weeklyProposal!;
  const { currentUser } = useAuth();
  const { showError, showSuccess } = useToast();
  const tz = useViewerZone();
  const [m, setM] = useState<LiveMembership | null | undefined>(undefined);
  useEffect(() => subscribeMembership(p.membershipId, setM), [p.membershipId]);
  // The server posts it as System: the plan says who the coach is.
  const coach = useUserIdentity(m?.coachId, 'coach');
  const player = useUserIdentity(m?.playerId, 'player');
  const [busy, setBusy] = useState<string | null>(null);
  const [picking, setPicking] = useState(false);
  const isCoach = !!m && currentUser?.uid === m.coachId;
  const coachFirst = firstNameOf(coach?.name ?? m?.coachName, 'Your coach');
  const playerFirst = firstNameOf(player?.name ?? (m as { playerName?: string } | null | undefined)?.playerName, 'Your player');
  const label = (s: (typeof p.slots)[number]) => (isCoach ? s.coachLabel : s.label);
  const chosen = p.chosen != null ? p.slots[p.chosen] : null;
  const lines = proposalLines({ status: p.status, chosenLabel: chosen ? label(chosen) : null, isCoach, coachFirst, playerFirst });
  // The player's headline is the server's own line ("Justin suggested a new weekly time").
  const headline = !isCoach && message.text ? message.text : lines.headline;
  const { line } = lines;
  // Tappable once the plan has said this is the player's card.
  const open = p.status === 'open' && !!m && !isCoach;

  const answer = async (key: string, a: { answer: 'accept' | 'keep' | 'pick'; slot?: number }) => {
    setBusy(key);
    try {
      // Accepting holds the month again: its one toast says so, not one per lesson (utils/oneToast).
      if (a.answer === 'accept') markBookingsQuiet();
      const r = await answerLiveWeeklyTimeProposal({ proposalId: p.proposalId, ...a });
      if (a.answer === 'accept' && r.line) showSuccess(weeklyTimeToast(r.line));
      if (a.answer === 'pick') setPicking(true);
    } catch (err) {
      showError(liveLessonsErrorMessage(err, 'Could not answer this suggestion. Please try again.'));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="my-3 flex justify-center" data-weekly-proposal={p.status}>
      <div className="w-full max-w-[320px] rounded-[12px] border border-[#e6e6e6] bg-white px-4 py-3.5">
        <div className="text-[13px] leading-[18px] text-db-text-2">{headline}</div>
        {open ? (
          <>
            <div className="mt-3 flex flex-col gap-2">
              {p.slots.map((s, i) => (
                <button key={`${s.weekday}-${s.time}`} type="button" disabled={!!busy} aria-busy={busy === `slot${i}`}
                  style={busyStyle(busy === `slot${i}`)} onClick={() => { void answer(`slot${i}`, { answer: 'accept', slot: i }); }}
                  data-proposal-slot
                  className="ios-press relative inline-flex h-11 w-full cursor-pointer items-center justify-center rounded-[100px] bg-[rgba(26,107,138,0.12)] px-4 text-[15px] leading-[20px] font-medium text-biscayne focus:outline-none focus-visible:ring-2 focus-visible:ring-db-accent/40 disabled:cursor-not-allowed">
                  {busy === `slot${i}` ? <ButtonSpinner size={16} /> : s.label}
                </button>
              ))}
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
              {p.hasWeeklyTime && (
                <button type="button" disabled={!!busy} aria-busy={busy === 'keep'} style={busyStyle(busy === 'keep')}
                  onClick={() => { void answer('keep', { answer: 'keep' }); }} className={BTN_SECONDARY_SM}>
                  {busy === 'keep' ? <ButtonSpinner size={16} /> : p.keepLabel}
                </button>
              )}
              <button type="button" disabled={!!busy} onClick={() => { void answer('pick', { answer: 'pick' }); }} className={TEXT_ACTION}>
                {p.pickLabel}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* The coach sees what was sent, in their own zone, until it is answered. */}
            {isCoach && p.status === 'open' && (
              <ul className="mt-1">
                {p.slots.map((s) => (
                  <li key={`${s.weekday}-${s.time}`} className="text-[17px] leading-[22px] font-semibold tracking-[-0.43px] text-db-text-1">{label(s)}</li>
                ))}
              </ul>
            )}
            {line && <div className="mt-2 text-[15px] leading-[20px] text-db-text-1">{line}</div>}
          </>
        )}
      </div>
      {picking && m && (
        <WeeklyTimeSheet membershipId={m.id} coachId={m.coachId} coachFirstName={coachFirst} tz={tz}
          initial={m.weeklyTime ? { weekday: m.weeklyTime.weekday, time: m.weeklyTime.time, lessonMode: m.weeklyTime.lessonMode } : null}
          onClose={(l) => { setPicking(false); if (l) showSuccess(weeklyTimeToast(l)); }} />
      )}
    </div>
  );
}
